// Copied from frontend/src/components/ui.tsx — keep visual language in sync manually.
import { useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent"
      style={{ width: size, height: size }}
      aria-label="loading"
    />
  );
}

export function PageHeader({ title, subtitle, right }: { title: string; subtitle?: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-end justify-between mb-4 md:mb-6 gap-3 flex-wrap">
      <div>
        <h1 className="text-xl md:text-2xl font-semibold">{title}</h1>
        {subtitle && <p className="text-sm text-muted mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function ErrorBox({ message }: { message: string }) {
  return (
    <div className="card p-3 text-sm text-bad border-bad/40 bg-bad/10">{message}</div>
  );
}

export function Switch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex w-11 h-6 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand/40 ${
        checked ? "bg-brand" : "bg-panel2 border border-border"
      } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block w-5 h-5 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

// Shared date navigation (circular prev/next around a centered label) used by
// the History and Optimization pages so all pages step dates identically.
export function DateStepper({
  label,
  sub,
  onPrev,
  onNext,
  canPrev = true,
  canNext = true,
}: {
  label: string;
  sub?: string;
  onPrev: () => void;
  onNext: () => void;
  canPrev?: boolean;
  canNext?: boolean;
}) {
  const btn =
    "inline-flex items-center justify-center h-9 w-9 rounded-full border border-border bg-panel2 text-muted enabled:hover:text-text disabled:opacity-30 disabled:cursor-not-allowed transition";
  return (
    <div className="flex items-center justify-center gap-4">
      <button type="button" className={btn} onClick={onPrev} disabled={!canPrev} aria-label="Previous">
        <ChevronLeft size={18} />
      </button>
      <div className="min-w-[8rem] text-center">
        <div className="text-base font-medium tabular-nums">{label}</div>
        {sub && <div className="text-xs text-muted">{sub}</div>}
      </div>
      <button type="button" className={btn} onClick={onNext} disabled={!canNext} aria-label="Next">
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

// Circular download button (matches the date-nav arrows) with a small dropdown.
// Each item is either a link (`href`) or an action (`onClick`).
export interface DownloadItem {
  label: string;
  href?: string;
  onClick?: () => void;
}
export function DownloadMenu({ items, label = "Download" }: { items: DownloadItem[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const circle =
    "inline-flex items-center justify-center h-9 w-9 rounded-full border border-border bg-panel2 text-muted hover:text-text transition";
  const item = "block w-full text-left px-3 py-1.5 text-sm rounded hover:bg-panel2 whitespace-nowrap";
  return (
    <div className="relative">
      <button type="button" className={circle} onClick={() => setOpen((o) => !o)} aria-label={label} title={label}>
        <Download size={16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 card p-1 min-w-[9rem] shadow-card">
            {items.map((it, i) =>
              it.href ? (
                <a key={i} className={item} href={it.href} onClick={() => setOpen(false)}>{it.label}</a>
              ) : (
                <button key={i} type="button" className={item} onClick={() => { it.onClick?.(); setOpen(false); }}>{it.label}</button>
              ),
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Trigger a client-side download of `data` serialized as pretty JSON.
export function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function fmt(value: number | string | undefined, digits = 2): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value;
  if (!Number.isFinite(value)) return "—";
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(digits);
}
